import { DeleteIcon, EditIcon, ExternalLinkIcon, ViewIcon, WarningIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Center,
    Divider,
    HStack,
    Input,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
    VStack
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { deletePatient, getAllFacilitiesOutside, getPatients } from '../../../api';
import AccessDenied from '../../../components/AccessDenied';
import Loading from '../../../components/Loading/Loading';
import Pagination from '../../../components/Pagination';
import PopoverId from '../../../components/PopoverId';
import TitleHeader from '../../../components/TitleHeader';
import { hasPermission } from '../../../Utils';
import PatientsValidationSend from './PatientValidationSend';

const PatientsPage = () => {
    const [patients, setPatients] = useState([]);
    const [searchParams, setSearchParams] = useState({
        name: '',
        dob: '' // YYYY-MM-DD
    });
    const [facilities, setFacilities] = useState([]);
    const [facilitySelect, setFacilitySelect] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const toast = useToast();
    const cancelRef = useRef();
    const navigate = useNavigate();
    const itemsPerPage = 25;

    const fetchData = async (page = 1, params = {}) => {
        setLoading(true);

        // Build query string explicitly (no helper)
        const qs = new URLSearchParams({
            page,
            limit: itemsPerPage,
            ...(params.name ? { name: params.name } : {}),
            ...(params.dob ? { dob: params.dob } : {}),
            ...(facilitySelect?.value ? { facility: facilitySelect.value } : {}),
        }).toString();



        const result = await getPatients(qs);
        if (result?.success) {
            console.log(result.data.patients)
            setPatients(result.data.patients || []);
            setCurrentPage(result.data.currentPage || page);
            setTotalPages(result.data.totalPages || 1);
        } else {
            setError(result?.error || 'Failed to load patients');
        }

        // facilities (outside list)
        try {
            const response = await getAllFacilitiesOutside();
            if (response.success) {
                setFacilities(response.data.facilities || []);
            }
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to load facilities.',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (id) => {
        // pass prebuilt query string (facility if selected)
        const qs = facilitySelect?.value ? `facility=${encodeURIComponent(facilitySelect.value)}` : '';
        const res = await deletePatient(id, qs);
        if (res.success) {
            toast({
                title: 'Deleted',
                description: 'Patient deleted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            setPatients(prev => prev.filter(p => p._id !== id));
        } else {
            toast({
                title: 'Error',
                description: res.message,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
        setDeleteTarget(null);
    };

    const handleSearch = async () => {
        setSearching(true);
        await fetchData(1, searchParams);
    };

    const handleClearSearch = () => {
        setSearchParams({ name: '', dob: '' });
        setFacilitySelect(null);
        setSearching(false);
        fetchData(1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (page) => {
        fetchData(page, searchParams);
    };

    if (error) return <AccessDenied msg={error} />;

    return (
        <Box maxW="full" mx="auto" p={8}>
            <VStack spacing={6} align="stretch">
                <TitleHeader
                    title="Patients"
                    buttonText="New Patient"
                    onClick={() => navigate('/reqs/patients/create')}
                />

                {/* Search */}
                <Text fontSize="lg">Search Patients</Text>

                <VStack spacing={4} mb={5} width="full">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="full">
                        <Input
                            name="name"
                            placeholder="Name (first or last)"
                            value={searchParams.name}
                            onChange={handleInputChange}
                        />
                        <Input
                            name="dob"
                            placeholder="DOB (MM/DD/YYYY)"
                            value={searchParams.dob}
                            onChange={handleInputChange}
                        />
                        <Select
                            options={facilities.map(fac => ({
                                value: fac._id,
                                label: `${fac.name} - ${fac.address?.city || 'N/A'}`
                            }))}
                            onChange={(option) => setFacilitySelect(option)}
                            value={facilitySelect}
                            placeholder="Select a facility"
                            isClearable
                        />
                    </SimpleGrid>

                    <HStack spacing={4}>
                        <Button onClick={handleSearch} colorScheme="teal" size="sm" isLoading={loading}>
                            Search
                        </Button>
                        {searching && (
                            <Button onClick={handleClearSearch} colorScheme="red" size="sm">
                                Clear
                            </Button>
                        )}
                    </HStack>
                </VStack>

                <Divider />

                {loading ? (
                    <Center mt="20%"><Loading /></Center>
                ) : (
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>Full Name</Th>
                                {/* <Th>Patient #</Th> */}
                                <Th>DOB</Th>
                                <Th>Phone</Th>
                                <Th>Email</Th>
                                <Th>Facility</Th>
                                <Th>Status</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {patients.map((p, index) => {
                                const hasMissingIdPhotos =
                                    !(p.governmentId?.frontImageUrl && p.governmentId?.backImageUrl);

                                const hasMissingInsPhotos =
                                    !(p.insurancePrimary?.cardFrontUrl && p.insurancePrimary?.cardBackUrl);

                                const today = new Date(); today.setHours(0, 0, 0, 0);
                                const exp = p.governmentId?.expiration ? new Date(p.governmentId.expiration) : null;
                                const isIdExpired = exp ? (exp.setHours(0, 0, 0, 0) < today.getTime()) : false;


                                const issues = [];

                                if (hasMissingIdPhotos) issues.push("Photo ID/DL missing");
                                if (hasMissingInsPhotos) issues.push("Insurance card missing");
                                if (isIdExpired) issues.push("Expired ID/DL");

                                const issuesText = issues.length > 0 ? issues : ["Complete"];

                                return (
                                    <Tr key={p._id} _hover={{ bg: 'gray.100' }}>
                                        <Td>
                                            <PopoverId
                                                place='Patients'
                                                index={index + (currentPage - 1) * itemsPerPage}
                                                content={p._id}
                                            />
                                        </Td>
                                        <Td>{[p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ')}</Td>
                                        {/* <Td>{p.patientNumber || 'N/A'}</Td> */}
                                        <Td>
                                            {p.dob ? new Date(p.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'N/A'}
                                        </Td>
                                        <Td>{p.phone || 'N/A'}</Td>
                                        <Td>{p.email || 'N/A'}</Td>
                                        <Td>
                                            <Text
                                                cursor="pointer"
                                                onClick={() => p.facility?._id && window.open(`/facilities/${p.facility._id}`, '_blank')}
                                                display="inline-flex"
                                                alignItems="center"
                                                color="blue.600"
                                            >
                                                {p.facility?.name || 'N/A'} <ExternalLinkIcon ml={1} />
                                            </Text>
                                        </Td>
                                        <Td>
                                            <VStack>
                                                {issuesText.map((issue, i) => (
                                                    <Badge key={i} colorScheme={issue == 'Complete' ? "green" : "red"}>{issue}</Badge>
                                                ))}
                                            </VStack>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Button
                                                    size="xs"
                                                    colorScheme="blue"
                                                    onClick={() => navigate(`/reqs/patients/view/${p._id}`)}
                                                >
                                                    <ViewIcon mr={1} /> View
                                                </Button>
                                                {hasPermission('updatePatient') && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="green"
                                                        onClick={() => navigate(`/reqs/patients/edit/${p._id}`)}
                                                    >
                                                        <EditIcon mr={1} /> Edit
                                                    </Button>
                                                )}


                                                {((issuesText.length > 0 && !issuesText.includes("Complete")) && hasPermission('sendPatientValidationLink')) &&
                                                    <Popover placement="top">
                                                        <PopoverTrigger>
                                                            <Button
                                                                size="xs"
                                                                colorScheme="orange"
                                                            >
                                                                <WarningIcon mr={1} /> Validation Required
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent>
                                                            <PopoverArrow />
                                                            <PopoverCloseButton />
                                                            <PopoverHeader fontWeight="bold">Validation Required</PopoverHeader>
                                                            <PopoverBody>
                                                                <Text mb={3}>
                                                                    Patient ( {[p.firstName, p.lastName].filter(Boolean).join(' ')} ) is missing
                                                                </Text>

                                                                {issuesText.map((issue, i) => (
                                                                    <Text key={i} as="li" mb={1} ml={4}>
                                                                        {issue}
                                                                    </Text>
                                                                ))}

                                                                <PatientsValidationSend patient={p} />
                                                            </PopoverBody>
                                                        </PopoverContent>
                                                    </Popover>
                                                }

                                                {hasPermission('deletePatient') && !p.deletedAt && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme="red"
                                                        onClick={() => setDeleteTarget(p)}
                                                    >
                                                        <DeleteIcon mr={1} /> Delete
                                                    </Button>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>

                    </Table>
                )}

                <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
            </VStack>

            {/* Delete Modal */}
            <AlertDialog
                isOpen={!!deleteTarget}
                leastDestructiveRef={cancelRef}
                onClose={() => setDeleteTarget(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Patient
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete{' '}
                            <strong>
                                {[deleteTarget?.firstName, deleteTarget?.middleName, deleteTarget?.lastName].filter(Boolean).join(' ')}
                            </strong>
                            ? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={() => handleDelete(deleteTarget._id)} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </Box>
    );
};

export default PatientsPage;
